/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { notificationInit } from '../../modules/notifications/actions';
import {
	getOrdersProcessInfoRequest,
	getPlanningsProcessListRequest,
	getCollectionUnplannedListRequest,
	getVeerouteConfigsRequest,
	selectPlanningRequest,
	getModesDictionaryRequest,
} from '../../api/requests';
import { REQUEST_ERROR_MESSAGE } from './constants';
import { DISMISS_TIME, VEEROUTE_URL } from '../../api/constants';
import {
	GET_PLANNINGS_PROCESS_LIST,
	setIsPlanningsProcessListLoadingAction,
	setPlanningsProcessListAction,
	setIsConfigsLoadingAction,
	setConfigsAction,
	GET_CONFIGS,
	setIsPlanningSelectedAction,
	SELECT_PLANNING,
	setIsProcessInfoLoadingAction,
	setProcessInfoAction,
	GET_PROCESS_INFO,
	GET_COLLECTION_LIST,
	resetIsCollectionListLoadingAction,
	setIsCollectionListLoadingAction,
	setCollectionListAction,
	setIsModesLoadingAction,
	setModesAction,
	GET_MODES,
} from './actions';
import { errorHandler } from '../../api/utils';
import { getGroupsListAction, getGroupsWithWaysListAction } from '../orders-groups/actions';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { getAddressLink } from '../../utils/getAddressLink';

export function* getPlanningsProcessListSaga({ payload: { process_id, searchQuery, noLoading } }) {
	try {
		if (!noLoading) {
			yield put(setIsPlanningsProcessListLoadingAction(true));
		}

		const { data, message, toast } = yield call(getPlanningsProcessListRequest, process_id, searchQuery);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setPlanningsProcessListAction(
					data?.map(planning => ({
						...planning,
						link: planning.experiment_key
							? {
									text: 'Перейти',
									target: '_blank',
									url: `${VEEROUTE_URL}/experiments/${planning.experiment_key}`,
							  }
							: null,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsPlanningsProcessListLoadingAction(false));
	}
}

export function* getProcessInfoSaga({ payload: { process_id, noLoading } }) {
	try {
		if (!noLoading) {
			yield put(setIsProcessInfoLoadingAction(true));
		}

		const { data, message, toast } = yield call(getOrdersProcessInfoRequest, process_id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setProcessInfoAction({
					...data,
				}),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsProcessInfoLoadingAction(false));
	}
}

export function* getConfigsSaga() {
	try {
		yield put(setIsConfigsLoadingAction(true));

		const { data, message, toast } = yield call(getVeerouteConfigsRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setConfigsAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsConfigsLoadingAction(false));
	}
}

export function* selectPlanningSaga({ payload: { planning_id, searchQuery, redirect } }) {
	try {
		yield put(setIsPlanningSelectedAction(planning_id));

		const { data, message, toast } = yield call(selectPlanningRequest, planning_id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));
			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			redirect();
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsPlanningSelectedAction(false));
	}
}

export function* getCollectionListSaga({ payload: { searchQuery, noLoading, row } }) {
	try {
		if (!noLoading) {
			yield put(setIsCollectionListLoadingAction(row.id));
		}

		const query = new URLSearchParams(searchQuery);
		query.delete('filters');
		query.delete('sorts');

		const str = removeModalQuery(
			query.toString().replace('childrenSorts', 'sorts').replace('childrenFilters', 'filters'),
		);

		const { data, message, toast } = yield call(
			getCollectionUnplannedListRequest,
			row.id,
			`?${str ? `${str}&receive=all` : 'receive=all'}`,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setCollectionListAction(
					row.id,
					data?.map(collection => ({
						...collection,
						points: collection.points.map(point => ({
							...point,
							address: getAddressLink(removeModalQuery(searchQuery), point.address, query),
						})),
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(resetIsCollectionListLoadingAction(row.id));
	}
}

export function* getModesSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setModesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export default function* ordersPlanningsContainerSaga() {
	yield takeEvery(GET_PLANNINGS_PROCESS_LIST, getPlanningsProcessListSaga);
	yield takeEvery(GET_PROCESS_INFO, getProcessInfoSaga);
	yield takeEvery(GET_CONFIGS, getConfigsSaga);
	yield takeEvery(SELECT_PLANNING, selectPlanningSaga);
	yield takeEvery(GET_COLLECTION_LIST, getCollectionListSaga);
	yield takeEvery(GET_MODES, getModesSaga);
}
